import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "motion/react";
import { FaHome, FaMobileAlt, FaUser, FaGithub, FaDownload, FaBars, FaTimes, FaSun, FaMoon } from "react-icons/fa";
import logoImg from "../Assets/logo.png";
import pdfCV from "../Assets/MRayyanCV.pdf";

function NavBar({ theme, toggleTheme }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/", icon: <FaHome /> },
    { name: "Apps & Works", path: "/project", icon: <FaMobileAlt /> },
    { name: "About & Skills", path: "/about", icon: <FaUser /> },
  ];

  return (
    <>
      {/* Scroll Progress Bar */}
      <motion.div className="scroll-progress-bar" style={{ scaleX }} />

      <header className={`modern-navbar-wrapper ${isScrolled ? "scrolled" : ""}`}>
        <div className="navbar-container">
          {/* Brand Logo with logo.png */}
          <Link to="/" className="brand-logo-link">
            <img src={logoImg} alt="Rayyan Waseem Logo" className="navbar-logo-img" />
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="desktop-nav-links">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link-item ${isActive ? "active" : ""}`}
                >
                  <span className="nav-icon">{link.icon}</span>
                  <span>{link.name}</span>
                  {isActive && (
                    <motion.div
                      className="nav-active-glow"
                      layoutId="activeNavIndicator"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Header Action Buttons */}
          <div className="navbar-right-actions">
            {/* Theme Toggle Button */}
            <button
              className="nav-action-btn theme-toggle-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? <FaSun className="theme-icon sun" /> : <FaMoon className="theme-icon moon" />}
            </button>

            {/* GitHub Profile */}
            <a
              href="https://github.com/SkullCrawler"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-action-btn github-btn"
              title="GitHub Profile"
            >
              <FaGithub />
            </a>

            {/* Direct Download CV Button */}
            <a
              href={pdfCV}
              download="Rayyan_Waseem_CV.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-action-btn download-cv-btn"
              title="Download Official Resume PDF"
            >
              <FaDownload /> <span>Resume</span>
            </a>

            {/* Mobile Hamburger Toggle */}
            <button
              className="mobile-menu-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <motion.div
            className="mobile-nav-drawer"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mobile-nav-items">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`mobile-nav-link ${isActive ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="m-icon">{link.icon}</span>
                    <span>{link.name}</span>
                  </Link>
                );
              })}
              <div className="mobile-nav-footer">
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn-modern secondary flex-1 w-100"
                    onClick={toggleTheme}
                  >
                    {theme === "dark" ? <FaSun /> : <FaMoon />} {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </button>
                </div>
                <a
                  href={pdfCV}
                  download="Rayyan_Waseem_CV.pdf"
                  className="btn-modern primary w-100 mb-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaDownload /> <span>Download Official CV</span>
                </a>
                <a
                  href="https://github.com/SkullCrawler"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-modern secondary w-100"
                >
                  <FaGithub /> <span>GitHub Profile</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </header>
    </>
  );
}

export default NavBar;
